/**
 * Modal de dépôt de réclamation depuis le relevé.
 * Affiche automatiquement la copie scannée (devoir / examen) si disponible.
 */
import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, FileSearch } from 'lucide-react';
import { portailApi, ReleveMatiere } from '../services/portailApi';

interface Props {
  matiere: ReleveMatiere;
  semestre: number;
  onClose: () => void;
  onSubmitted: () => void;
}

interface ScanInfo {
  type: 'devoir' | 'examen' | 'rattrapage';
  libelle: string;
  image_base64: string | null;
}

export const ReclamationModal: React.FC<Props> = ({ matiere, semestre, onClose, onSubmitted }) => {
  const [typeRec, setTypeRec] = useState<'devoir' | 'examen' | 'rattrapage'>('examen');
  const [noteAttendue, setNoteAttendue] = useState<string>('');
  const [motif, setMotif] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [scans, setScans] = useState<ScanInfo[]>([]);
  const [scanOpen, setScanOpen] = useState(true);
  const [limiteSemestre, setLimiteSemestre] = useState<number | null>(null);
  const [nbDejaUtilisees, setNbDejaUtilisees] = useState<number | null>(null);

  // Charger les scans disponibles à l'ouverture
  useEffect(() => {
    portailApi.getCopiesScan(matiere.matiere_id)
      .then((res) => setScans(res.scans || []))
      .catch(() => setScans([]));
  }, [matiere.matiere_id]);

  // Charger la limite de réclamations configurée + le nombre déjà utilisé ce semestre
  useEffect(() => {
    portailApi.mesReclamations()
      .then((res) => {
        setLimiteSemestre(res.limite_par_semestre);
        const semestreLabel = `S${semestre}`;
        const nb = (res.results || []).filter(
          (r: any) => r.semestre === semestreLabel
        ).length;
        setNbDejaUtilisees(nb);
      })
      .catch(() => { /* silencieux : la modal reste utilisable */ });
  }, [semestre]);

  const scanCourant = scans.find((s) => s.type === typeRec) || null;

  const noteActuelle =
    typeRec === 'devoir'
      ? matiere.moy_devoirs
      : typeRec === 'rattrapage'
      ? (matiere.note_examen_rattrapage ?? null)
      : matiere.note_examen;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (motif.trim().length < 10) {
      setError('Le motif doit faire au moins 10 caractères.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await portailApi.deposerReclamation({
        type_reclamation: typeRec,
        matiere_id: matiere.matiere_id,
        matiere_nom: matiere.nom_matiere,
        semestre: `S${semestre}`,
        note_actuelle: noteActuelle,
        note_attendue: noteAttendue ? parseFloat(noteAttendue) : null,
        motif: motif.trim(),
      });
      setSuccess(true);
      setTimeout(() => onSubmitted(), 1500);
    } catch (e: any) {
      setError(e.message || 'Erreur lors du dépôt');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-gray-900 rounded-t-lg sm:rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-gray-800 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-100">Déposer une réclamation</h3>
            <p className="text-xs text-gray-400 mt-0.5">{matiere.nom_matiere}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-300 text-2xl leading-none">×</button>
        </div>

        {success ? (
          <div className="p-8 text-center">
            <div className="w-12 h-12 mx-auto mb-3 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-green-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h4 className="font-semibold text-gray-100">Réclamation déposée</h4>
            <p className="text-sm text-gray-400 mt-2">
              La scolarité l'examinera dans les meilleurs délais.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Type de note
              </label>
              <div className={`grid gap-2 ${matiere.note_examen_rattrapage != null ? 'grid-cols-3' : 'grid-cols-2'}`}>
                <button
                  type="button"
                  onClick={() => setTypeRec('devoir')}
                  className={`p-3 rounded border text-sm transition-colors ${
                    typeRec === 'devoir' ? 'border-blue-500 bg-blue-900/30 text-blue-300' : 'border-gray-700 text-gray-200 hover:bg-gray-800/50'
                  }`}
                >
                  <div className="font-medium">Devoir</div>
                  <div className="text-xs opacity-75 mt-0.5">{matiere.moy_devoirs?.toFixed(2) ?? '—'}</div>
                </button>
                <button
                  type="button"
                  onClick={() => setTypeRec('examen')}
                  className={`p-3 rounded border text-sm transition-colors ${
                    typeRec === 'examen' ? 'border-blue-500 bg-blue-900/30 text-blue-300' : 'border-gray-700 text-gray-200 hover:bg-gray-800/50'
                  }`}
                >
                  <div className="font-medium">Examen</div>
                  <div className="text-xs opacity-75 mt-0.5">{matiere.note_examen?.toFixed(2) ?? '—'}</div>
                </button>
                {matiere.note_examen_rattrapage != null && (
                  <button
                    type="button"
                    onClick={() => setTypeRec('rattrapage')}
                    className={`p-3 rounded border text-sm transition-colors ${
                      typeRec === 'rattrapage' ? 'border-orange-500 bg-orange-900/30 text-orange-300' : 'border-gray-700 text-gray-200 hover:bg-gray-800/50'
                    }`}
                  >
                    <div className="font-medium">Rattrapage</div>
                    <div className="text-xs opacity-75 mt-0.5">{matiere.note_examen_rattrapage.toFixed(2)}</div>
                  </button>
                )}
              </div>
            </div>

            {/* Aperçu du scan correspondant au type sélectionné */}
            <div className="border border-gray-700 rounded overflow-hidden">
              <button
                type="button"
                onClick={() => setScanOpen(!scanOpen)}
                className="w-full flex items-center justify-between px-3 py-2 bg-gray-800/50 hover:bg-gray-800 text-sm"
              >
                <span className="flex items-center gap-2 text-gray-200">
                  <FileSearch size={14} className="text-blue-400" />
                  {scanCourant?.image_base64
                    ? `Copie scannée (${typeRec === 'devoir' ? 'devoir' : 'examen'})`
                    : `Scan non disponible pour ce ${typeRec}`}
                </span>
                {scanOpen ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
              </button>
              {scanOpen && (
                <div className="p-2 bg-gray-950">
                  {scanCourant?.image_base64 ? (
                    <>
                      <div className="bg-white rounded overflow-hidden max-h-[200px] overflow-y-auto">
                        <img
                          src={`data:image/jpeg;base64,${scanCourant.image_base64}`}
                          alt="Copie scannée"
                          className="w-full block"
                        />
                      </div>
                      <p className="text-[10px] text-gray-500 italic text-center mt-2">
                        Vérifiez bien que la note diffère de votre copie avant de réclamer.
                      </p>
                    </>
                  ) : (
                    <div className="text-center py-4 text-xs text-gray-400">
                      Le {typeRec === 'devoir' ? 'relevé de devoirs' : 'relevé d\'examen'} pour cette
                      matière n'a pas été scanné. Vous pouvez tout de même déposer votre réclamation.
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-200 mb-1">
                Note attendue (optionnel)
              </label>
              <input
                type="number" step="0.5" min="0" max="20"
                value={noteAttendue}
                onChange={(e) => setNoteAttendue(e.target.value)}
                placeholder="Ex: 14"
                className="w-full px-3 py-2 border border-gray-700 rounded text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-200 mb-1">
                Motif (10 caractères minimum) *
              </label>
              <textarea
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
                rows={4} required minLength={10}
                placeholder="Expliquez clairement le problème (erreur de saisie, copie non corrigée, etc.)"
                className="w-full px-3 py-2 border border-gray-700 rounded text-sm"
              />
              <p className="text-xs text-gray-400 mt-1">{motif.length}/500 caractères</p>
            </div>

            {error && (
              <div className="p-3 bg-red-900/30 border border-red-800 rounded text-sm text-red-300">{error}</div>
            )}

            <div className="bg-gray-800/50 border border-gray-800 rounded p-3 text-xs text-gray-300">
              {limiteSemestre !== null ? (
                <>
                  Limite de {limiteSemestre} réclamation{limiteSemestre > 1 ? 's' : ''} par semestre
                  {nbDejaUtilisees !== null && (
                    <> ({nbDejaUtilisees}/{limiteSemestre} déjà utilisée{nbDejaUtilisees > 1 ? 's' : ''} ce semestre)</>
                  )}.
                </>
              ) : (
                <>Limite de réclamations par semestre selon la configuration scolarité.</>
              )}
              {' '}Une réclamation déjà en cours sur cette matière empêche une nouvelle.
            </div>

            <div className="flex gap-2">
              <button
                type="button" onClick={onClose}
                className="flex-1 py-2.5 bg-gray-900 border border-gray-700 hover:bg-gray-800/50 text-gray-200 rounded font-medium text-sm"
              >Annuler</button>
              <button
                type="submit" disabled={loading}
                className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium disabled:opacity-50 text-sm"
              >{loading ? 'Envoi...' : 'Déposer'}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
